import React from 'react'
import FocusLock from 'react-focus-lock'
import { useOnClickOutside } from '@deseretbook/react-utils'
import { ScrollLocky } from 'react-scroll-locky'
import PropTypes from 'prop-types'
import { styled, Button, Box, Text, Icon } from '@deseretbook/react-ui'

import './MobileDrawer.css'

/**
 * The main Drawer wrapper. All the Drawer stuff goes inside this.
 */
const StyledDrawer = styled('div')(() => ({
  label: 'Drawer',
}))

const StyledDrawerWrapper = styled('div')(({ theme, isOpen }) => {
  return {
    label: 'DrawerWrapper',
    position: 'fixed',
    display: 'flex',
    pointerEvents: 'none', // Ignore pointer events so they pass through to the overlay.
    transition: isOpen
      ? 'transform 150ms ease, visibility 0ms'
      : 'transform 150ms ease, visibility 0ms 150ms',
    visibility: isOpen ? 'visible' : 'hidden',
    zIndex: 1000000,
    bottom: 0,
    left: 0,
    maxHeight: '100%',
    width: '100%',
    transform: isOpen ? 'translateY(0)' : `translateY(${theme.spacing.use(30)})`,
    alignItems: 'flex-end',
    flexDirection: 'column',
  }
})

/**
 * The Drawer Container. This handles the position and size of the actual Drawer.
 */
const StyledDrawerContentContainer = styled('div')(({ theme, position }) => ({
  label: 'DrawerContentContainer',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  height: '100%',
  width: ['left', 'right'].indexOf(position) > -1 ? theme.spacing.use(25) : '100%',
  backgroundColor: theme.palette.background.main,
  pointerEvents: 'auto', // Re-enable pointer events in content.
}))

/**
 * The main content component. This is where all content present in the drawer
 * should go.
 */
const StyledDrawerContent = styled('div')(() => ({
  label: 'DrawerContent',
  flex: 1,
  padding: '12px 24px',
  overflowY: 'auto',
  overflowX: 'hidden',
}))

const StyledDrawerHeader = styled('header')(() => ({
  label: 'DrawerHeader',
  padding: '12px 24px',
  backgroundColor: '#f4f6f6',
  color: 'black',
  width: '100%',
}))

const StyledDrawerFooter = styled('footer')(({ theme }) => ({
  label: 'DrawerFooter',
  padding: theme.spacing.use(0.5, 1),
  boxShadow: `0 -2px 0 0 ${theme.palette.divider.main}`,
}))

/**
 * Drawer component. Passes necessary props to the various children of the Drawer,
 * this ensures all parts of the Drawer work together.
 *
 * @param {object} param - params
 *
 * @returns {function} Drawer component
 */
const MobileDrawer = ({ children, position, isOpen, close, title, ...rest }) => {
  const drawerRef = React.useRef()

  /**
   * Keydown event handler. In the scope of an open modal, this listens for any 'Esc' key press in
   * order to close the modal.
   * @param {Event} e - Keydown event.
   */
  const onKeydown = React.useCallback(
    (e) => {
      if (e && e.key === 'Escape' && close && typeof close === 'function') {
        close()
      }
    },
    [close],
  )

  useOnClickOutside([drawerRef], close || (() => {}))

  React.useEffect(() => {
    // We only want to listen for events while the popup is open.
    if (isOpen) {
      document.addEventListener('keydown', onKeydown, false)
    }

    return () => {
      document.removeEventListener('keydown', onKeydown)
    }
  }, [isOpen, onKeydown])

  return (
    <ScrollLocky enabled={isOpen} noRelative isolation={false}>
      <FocusLock disabled={!isOpen} returnFocus>
        <StyledDrawer isOpen={isOpen}>
          <StyledDrawerWrapper ref={drawerRef} position={position} isOpen={isOpen}>
            <StyledDrawerContentContainer
              {...rest}
              position={position}
              role="dialog"
              aria-modal="true"
              aria-hidden={!isOpen}
              data-autofocus-inside
            >
              <StyledDrawerHeader>
                <Box alignItems="center" justifyContent="space-between">
                  <Button id="mobile-drawer-button" onClick={close}>
                    <Icon id="mobile-drawer-icon" name="chevronDownSlim" />
                  </Button>
                  <Text
                    style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '16px' }}
                  >
                    {title}
                  </Text>
                  <Box />
                </Box>
              </StyledDrawerHeader>
              {children}
            </StyledDrawerContentContainer>
          </StyledDrawerWrapper>
        </StyledDrawer>
      </FocusLock>
    </ScrollLocky>
  )
}

// Attach exposed children to the Drawer
MobileDrawer.Content = StyledDrawerContent
MobileDrawer.Header = StyledDrawerHeader
MobileDrawer.Footer = StyledDrawerFooter

MobileDrawer.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  position: PropTypes.oneOf(['bottom', 'top', 'right', 'left']),
  close: PropTypes.func,
  title: PropTypes.string,
}

MobileDrawer.defaultProps = {
  children: false,
  isOpen: false,
  position: 'bottom',
  close: () => {},
  title: '',
}

export default MobileDrawer
